import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Search, Phone, Mail, X, User, Car, CalendarPlus, CalendarCheck, StickyNote, ShoppingBag,
  History, Wrench, Check, Timer, ClipboardList, Archive, Hammer,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { formatCurrency, formatDate } from '../mock';
import { useAuth } from '../context/AuthContext';
import { useDeepLink } from '../hooks/useDeepLink';
import { InspectionReport } from '../components/InspectionReport';
import { NewCaseDialog, WarrantyBadge, WarrantyPanel } from './postsale/NewCaseDialog';

export const elapsedH = (start, end) => {
  const ms = (end ? new Date(end) : new Date()) - new Date(start);
  if (isNaN(ms) || ms < 0) return '0h 0m';
  const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
};
export const DELIVER_ROLES = ['Service Manager', 'Service Advisor', 'Service Inspector', 'Post-Sale Manager', 'Owner', 'Administrator', 'General Manager'];

const STATUSES = ['New', 'Assigned', 'Customer Contacted', 'Appointment Scheduled', 'Waiting Customer', 'Service', 'Waiting Parts', 'Resolved', 'Closed'];
const WORK_STATUSES = ['Não iniciado', 'Iniciado', 'Finalizado'];
const LOCATIONS = ['Minha oficina', 'Oficina externa'];
const workCls = (s) => (s === 'Finalizado' ? 't-ok' : s === 'Iniciado' ? 't-warn' : '');
const prioCls = { Alta: 't-bad', Média: 't-warn', Baixa: '' };
const statusCls = (s) => (['Resolved', 'Closed'].includes(s) ? 't-ok' : s === 'New' ? 't-bad' : 't-warn');
const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();
const deptStyle = {
  'BDC/CRM': 'text-sky-400', 'F&I': 'text-violet-400', SERVICE: 'text-blue-400', DELIVERY: 'text-emerald-400',
  NOTE: 'text-amber-400', 'EDIÇÃO': 'text-rose-400', 'PÓS-VENDA': 'text-orange-400',
};
const fmtTs = (iso) => { try { const d = new Date(iso); return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`; } catch { return iso; } };

const CaseWorkspace = ({ caseId, cases, onClose, onChanged }) => {
  const c = cases.find((x) => x.id === caseId);
  const [journey, setJourney] = useState(null);
  const [note, setNote] = useState('');
  const [schedOpen, setSchedOpen] = useState(false);
  const [showInsp, setShowInsp] = useState(false);
  const [sched, setSched] = useState({ data: '', hora: '09:00' });

  useEffect(() => {
    setJourney(null);
    if (c?.customerId) api.get(`/customers/${c.customerId}/journey`).then((r) => setJourney(r.data)).catch(() => {});
  }, [caseId]); // eslint-disable-line

  if (!c) return null;
  const cust = journey?.customer;
  const deals = journey?.deals || [];
  const events = (() => {
    const ev = [...(journey?.events || [])];
    (c.notes || []).forEach((n) => ev.push({ ts: n.timestamp, dept: 'PÓS-VENDA', action: 'Note (Pós-venda)', user: n.user_name, note: n.note }));
    ev.push({ ts: c.created_at, dept: 'PÓS-VENDA', action: `Caso de pós-venda criado — ${c.tipo}`, user: c.createdBy, note: c.reclamacao });
    return ev.filter((e) => e.ts).sort((a, b) => (b.ts > a.ts ? 1 : -1));
  })();

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/post-sale/cases/${c.id}/note`, { note: note.trim() }); setNote(''); onChanged(); }
    catch (e) { toast({ title: 'Erro ao salvar note' }); }
  };
  const changeStatus = async (status) => {
    try { await api.put(`/post_sale_cases/${c.id}`, { ...c, status }); onChanged(); }
    catch (e) { toast({ title: 'Erro' }); }
  };
  const changeWork = async (patch) => {
    try { await api.put(`/post-sale/cases/${c.id}/work`, patch); onChanged(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const schedule = async () => {
    if (!sched.data) { toast({ title: 'Informe a data' }); return; }
    try {
      await api.post(`/post-sale/cases/${c.id}/schedule`, sched);
      toast({ title: 'Serviço agendado', description: `${formatDate(sched.data)} ${sched.hora} — visível na Agenda` });
      setSchedOpen(false); onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-2 sm:p-5" data-testid="ps-workspace">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative z-10 w-full max-w-[1180px] h-[92vh] rounded-[3px] border border-border bg-card overflow-hidden flex flex-col">
        {/* header */}
        <div className="shrink-0 border-b border-border px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="w-3 h-9 rounded-[1px] bg-orange-400 shrink-0" />
          <div className="min-w-0">
            <h2 className="font-display font-bold text-[17px] leading-tight truncate" data-testid="ps-ws-name">{c.cliente}</h2>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="mono">{c.id}</span>
              {(cust?.telefone || c.telefone) && <a href={`tel:${cust?.telefone || c.telefone}`} className="flex items-center gap-1 hover:text-foreground num"><Phone className="w-3 h-3" />{cust?.telefone || c.telefone}</a>}
              {(cust?.email || c.email) && <a href={`mailto:${cust?.email || c.email}`} className="flex items-center gap-1 hover:text-foreground truncate"><Mail className="w-3 h-3" />{cust?.email || c.email}</a>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap ml-2">
            <span className="ent-tag t-warn">{c.tipo}</span>
            <span className={`ent-tag ${prioCls[c.prioridade] || ''}`}>{c.prioridade}</span>
            <select data-testid="ps-ws-status" value={c.status} onChange={(e) => changeStatus(e.target.value)} className="ent-input" style={{ width: 'auto' }}>
              {STATUSES.map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
            </select>
          </div>
          <button data-testid="ps-ws-close" onClick={onClose} className="ent-btn ent-btn-icon ml-auto"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 overflow-y-auto lg:overflow-hidden">
          {/* left: caso + cliente */}
          <div className="lg:col-span-3 lg:overflow-y-auto border-b lg:border-b-0 lg:border-r border-border p-3 space-y-3">
            <div className="ent-panel p-3 space-y-2">
              <p className="ent-label flex items-center gap-1.5"><Wrench className="w-3 h-3 text-orange-400" /> Caso</p>
              {c.veiculo && <p className="text-[12.5px] font-semibold flex items-center gap-1.5"><Car className="w-3.5 h-3.5 text-primary" /> {c.veiculo}</p>}
              <p className="mono text-[11px] t-label" data-testid="ps-ws-vin">VIN: {c.vin || '— não informado'}</p>
              {c.reclamacao && <p className="text-[12px] text-foreground/75 border-l-2 border-border pl-2.5">{c.reclamacao}</p>}
              <p className="text-[11px] text-muted-foreground">Responsável: <b className="text-foreground/80">{c.responsavel || '—'}</b></p>
              <p className="text-[11px] t-label">Aberto por {c.createdBy} · {fmtTs(c.created_at)}</p>
            </div>
            <WarrantyPanel c={c} onChanged={onChanged} />
            <div className="ent-panel p-3 space-y-2" data-testid="ps-ws-work">
              <p className="ent-label flex items-center gap-1.5"><Hammer className="w-3 h-3 text-primary" /> Execução</p>
              <div>
                <p className="ent-label mb-1">Status do trabalho</p>
                <select data-testid="ps-ws-work-status" value={c.work_status || 'Não iniciado'} onChange={(e) => changeWork({ work_status: e.target.value })} className={`ent-input w-full ${workCls(c.work_status)}`}>
                  {WORK_STATUSES.map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
                </select>
              </div>
              <div>
                <p className="ent-label mb-1">Onde está sendo feito</p>
                <select data-testid="ps-ws-local" value={c.service_location || ''} onChange={(e) => changeWork({ service_location: e.target.value })} className="ent-input w-full">
                  <option value="" className="bg-popover text-foreground">Informar local…</option>
                  {LOCATIONS.map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
                </select>
                {c.service_location === 'Oficina externa' && (
                  <input data-testid="ps-ws-shop" defaultValue={c.external_shop || ''} placeholder="Nome da oficina externa"
                    onBlur={(e) => e.target.value !== (c.external_shop || '') && changeWork({ external_shop: e.target.value })}
                    className="ent-input w-full mt-1.5" />
                )}
              </div>
              {c.work_started_at && <p className="text-[11px] t-label num">Iniciado em {fmtTs(c.work_started_at)}</p>}
              {c.work_finished_at && <p className="text-[11px] t-label num">Finalizado em {fmtTs(c.work_finished_at)}{c.work_started_at ? ` · ${elapsedH(c.work_started_at, c.work_finished_at)}` : ''}</p>}
            </div>
            <div className="ent-panel p-3 space-y-2">
              <p className="ent-label flex items-center gap-1.5"><User className="w-3 h-3 text-primary" /> Cliente</p>
              {cust ? (
                <>
                  <p className="text-[12px] text-muted-foreground">Cliente desde <b className="text-foreground/80">{formatDate(cust.criadoEm)}</b> · {cust.origem}</p>
                  <div className="grid grid-cols-3 border-t border-border pt-2">
                    <div><p className="ent-label">Visitas</p><p className="ent-value mt-0.5">{cust.totalAtendimentos || 0}</p></div>
                    <div><p className="ent-label">Compras</p><p className="ent-value mt-0.5">{deals.length}</p></div>
                    <div><p className="ent-label">Agendas</p><p className="ent-value mt-0.5">{(journey?.appointments || []).length}</p></div>
                  </div>
                </>
              ) : <p className="text-[12px] t-label">Carregando dados do cliente...</p>}
            </div>
            {deals.length > 0 && (
              <div className="ent-panel p-3 space-y-2">
                <p className="ent-label flex items-center gap-1.5"><ShoppingBag className="w-3 h-3 text-emerald-400" /> Compras</p>
                {deals.map((d) => (
                  <div key={d.id} className="border-t border-border pt-2 first:border-0 first:pt-0">
                    <p className="text-[12px] font-medium">{d.vehicle_desc}</p>
                    <p className="text-[11px] t-label num">{formatDate(d.sale_date)} · {d.salesperson_name} · {formatCurrency(d.sale_price)}</p>
                  </div>
                ))}
              </div>
            )}
            {(c.veiculo || deals[0]?.vehicle_id) && (
              <div className="ent-panel p-3 space-y-2" data-testid="ps-ws-inspection">
                <button onClick={() => setShowInsp((s) => !s)} data-testid="ps-ws-inspection-toggle"
                  className="w-full flex items-center justify-between gap-2 text-left">
                  <span className="ent-label flex items-center gap-1.5"><Wrench className="w-3 h-3 text-primary" /> Laudo da inspeção</span>
                  <span className="ent-btn ent-btn-sm">{showInsp ? 'Ocultar' : 'Ver laudo'}</span>
                </button>
                {showInsp && <InspectionReport vehicleId={deals[0]?.vehicle_id || ''} query={deals[0]?.vehicle_id ? '' : (c.veiculo || '')} />}
              </div>
            )}
          </div>

          {/* center: histórico */}
          <div className="lg:col-span-6 flex flex-col min-h-[380px] lg:min-h-0">
            <div className="shrink-0 px-4 pt-3 pb-2 border-b border-border">
              <h3 className="text-[13px] font-semibold flex items-center gap-2"><History className="w-3.5 h-3.5 text-primary" /> Histórico do cliente</h3>
              <p className="text-[11px] t-label">Toda a jornada desde o nascimento como lead</p>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="ps-ws-timeline">
              {events.map((e, i) => (
                <div key={i} className="flex gap-3 px-4 py-2.5 border-b border-border/60 hover:bg-primary/[0.04] transition-colors">
                  <span className={`shrink-0 ent-tag ${deptStyle[e.dept] || ''}`}>{e.dept}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-medium">{e.action}</p>
                    <p className="text-[11px] t-label num">{fmtTs(e.ts)}{e.user ? ` · ${e.user}` : ''}</p>
                    {e.note && <p className="text-[12px] text-foreground/65 mt-1 border-l-2 border-border pl-2.5">{e.note}</p>}
                  </div>
                </div>
              ))}
              {events.length === 0 && <p className="ent-empty">Sem histórico registrado</p>}
            </div>
            <div className="shrink-0 border-t border-border px-4 py-2.5 flex gap-2">
              <input data-testid="ps-ws-note-input" className="ent-input" placeholder="Adicionar note ao caso..." value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNote()} />
              <button data-testid="ps-ws-note-save" onClick={addNote} disabled={!note.trim()} className="ent-btn ent-btn-primary disabled:opacity-40"><StickyNote className="w-3.5 h-3.5" /> Note</button>
            </div>
          </div>

          {/* right: agendamento + notes */}
          <div className="lg:col-span-3 lg:overflow-y-auto border-t lg:border-t-0 lg:border-l border-border p-3 space-y-3">
            <div className="ent-panel p-3" data-testid="ps-ws-schedule" style={{ borderTop: `2px solid ${c.agendado ? '#10b981' : '#3b82f6'}` }}>
              <p className="ent-label mb-1.5">Serviço</p>
              {c.agendado ? (
                <>
                  <p className="text-[12.5px] font-semibold flex items-center gap-1.5 t-ok"><CalendarCheck className="w-3.5 h-3.5" /> Agendado</p>
                  <p className="text-[12px] text-muted-foreground mt-1 num">{formatDate(c.appointment_date)} às {c.appointment_time || '—'}</p>
                  <p className="text-[10.5px] t-label mt-1">Visível na Agenda da loja</p>
                </>
              ) : schedOpen ? (
                <div className="space-y-2">
                  <input type="date" className="ent-input" value={sched.data} onChange={(e) => setSched({ ...sched, data: e.target.value })} data-testid="ps-sched-date" />
                  <input type="time" className="ent-input" value={sched.hora} onChange={(e) => setSched({ ...sched, hora: e.target.value })} data-testid="ps-sched-time" />
                  <div className="flex gap-2">
                    <button onClick={() => setSchedOpen(false)} className="ent-btn flex-1 justify-center">Cancelar</button>
                    <button data-testid="ps-sched-save" onClick={schedule} className="ent-btn ent-btn-primary flex-1 justify-center">Agendar</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-[12px] t-label">Nenhum serviço agendado para este caso.</p>
                  <button data-testid="ps-ws-schedule-btn" onClick={() => setSchedOpen(true)} className="ent-btn w-full justify-center mt-2 text-primary border-primary/50"><CalendarPlus className="w-3.5 h-3.5" /> Agendar serviço</button>
                </>
              )}
            </div>
            <div className="ent-panel p-3 space-y-2">
              <p className="ent-label">Notes do caso ({(c.notes || []).length})</p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {(c.notes || []).slice().reverse().map((n) => (
                  <div key={n.id} className="border-t border-border pt-2 first:border-0 first:pt-0">
                    <p className="text-[12px]">{n.note}</p>
                    <p className="text-[10.5px] t-label num mt-0.5">{n.user_name} · {fmtTs(n.timestamp)}</p>
                  </div>
                ))}
                {(c.notes || []).length === 0 && <p className="text-[12px] t-label">Nenhuma note ainda.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function PostSale() {
  const { user } = useAuth();
  const canDeliver = DELIVER_ROLES.includes(user?.role);
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [dOpen, setDOpen] = useState(false);
  const [wsEta, setWsEta] = useState({ id: '', date: '' });
  const [viewTab, setViewTab] = useState('ativos');
  const [openedBy, setOpenedBy] = useState('');
  const [, setTick] = useState(0);

  const load = async () => {
    try {
      const cs = await api.get('/post_sale_cases');
      setCases(cs.data.sort((a, b) => ((b.created_at || '') > (a.created_at || '') ? 1 : -1)));
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);
  useDeepLink('case', setOpenId);
  useEffect(() => { const iv = setInterval(() => setTick((t) => t + 1), 30000); return () => clearInterval(iv); }, []);

  const workshop = async (c, action, extra = {}) => {
    try {
      await api.post(`/post-sale/cases/${c.id}/workshop`, { action, ...extra });
      const msgs = { received: 'Caso marcado como recebido', in_workshop: 'Carro na oficina — contador de horas iniciado', delivered: 'Carro entregue — contador finalizado' };
      toast({ title: msgs[action] || 'Atualizado' });
      setWsEta({ id: '', date: '' }); load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const changeStatus = async (c, status) => {
    setCases((prev) => prev.map((x) => (x.id === c.id ? { ...x, status } : x)));
    try { await api.put(`/post_sale_cases/${c.id}`, { ...c, status }); } catch (e) { load(); }
  };

  const changeWork = async (c, patch) => {
    setCases((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...patch } : x)));
    try { await api.put(`/post-sale/cases/${c.id}/work`, patch); load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); load(); }
  };

  const ativos = cases.filter((c) => !c.delivered && !c.archived);
  const arquivados = cases.filter((c) => c.delivered || c.archived);
  const base = viewTab === 'ativos' ? ativos : arquivados;
  const filtered = base.filter((c) => (!q || `${c.cliente} ${c.veiculo} ${c.vin || ''} ${c.telefone || ''} ${c.tipo} ${c.id} ${c.createdBy || ''}`.toLowerCase().includes(q.toLowerCase()))
    && (!openedBy || (c.createdBy || '') === openedBy));

  const openers = useMemo(() => {
    const m = new Map();
    cases.forEach((c) => c.createdBy && m.set(c.createdBy, (m.get(c.createdBy) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [cases]);

  const semAgenda = ativos.filter((c) => !c.in_workshop && !c.agendado);
  const agendados = ativos.filter((c) => !c.in_workshop && c.agendado);
  const naOficina = ativos.filter((c) => c.in_workshop);
  const emExecucao = ativos.filter((c) => c.work_status === 'Iniciado');
  const externos = ativos.filter((c) => c.service_location === 'Oficina externa');
  const entregues = cases.filter((c) => c.delivered);
  const avgHours = entregues.length ? (entregues.reduce((s, c) => s + (Number(c.workshop_hours) || 0), 0) / entregues.length).toFixed(1) : '—';

  const WorkshopCell = ({ c }) => {
    if (c.delivered) {
      return (
        <span className="ent-status t-ok" data-testid={`ps-delivered-${c.id}`}>
          <Check className="w-3 h-3" /> Entregue · {c.workshop_hours}h
        </span>
      );
    }
    if (c.in_workshop) {
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="ent-status t-warn num" data-testid={`ps-timer-${c.id}`}><Timer className="w-3 h-3" /> {elapsedH(c.workshop_started_at)}</span>
          {c.eta_date && <span className="ent-tag num">ETA {formatDate(c.eta_date)}</span>}
          {canDeliver
            ? <button data-testid={`ps-deliver-${c.id}`} onClick={() => workshop(c, 'delivered')} className="ent-btn ent-btn-sm text-[hsl(var(--ok))] border-[hsl(var(--ok))]/50"><Check className="w-3 h-3" /> Entregue</button>
            : <span className="text-[10.5px] t-label">Só o Service entrega</span>}
        </div>
      );
    }
    if (wsEta.id === c.id) {
      return (
        <div className="flex items-center gap-1.5" data-testid={`ps-eta-form-${c.id}`}>
          <input type="date" className="ent-input" style={{ width: 132 }} value={wsEta.date} onChange={(e) => setWsEta({ ...wsEta, date: e.target.value })} data-testid={`ps-eta-date-${c.id}`} />
          <button data-testid={`ps-eta-confirm-${c.id}`} onClick={() => workshop(c, 'in_workshop', { eta_date: wsEta.date })} className="ent-btn ent-btn-sm text-[hsl(var(--warn))] border-[hsl(var(--warn))]/50">Iniciar</button>
          <button onClick={() => setWsEta({ id: '', date: '' })} className="ent-btn ent-btn-icon border-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        {!c.received && <button data-testid={`ps-received-${c.id}`} onClick={() => workshop(c, 'received')} className="ent-btn ent-btn-sm">Recebido</button>}
        {c.received && <span className="ent-tag" title={`Recebido por ${c.received_by}`}>Recebido</span>}
        <button data-testid={`ps-workshop-${c.id}`} onClick={() => setWsEta({ id: c.id, date: c.eta_date || '' })} className="ent-btn ent-btn-sm text-[hsl(var(--warn))] border-[hsl(var(--warn))]/50"><Wrench className="w-3 h-3" /> Oficina</button>
      </div>
    );
  };

  const CaseTable = ({ list }) => (
    <div className="overflow-x-auto">
      <table className="ent-table">
        <thead>
          <tr>
            <th>Cliente</th>
            <th className="hidden lg:table-cell">Veículo</th>
            <th>Tipo</th>
            <th className="hidden xl:table-cell">Prioridade</th>
            <th>Status</th>
            <th>Trabalho</th>
            <th>Local</th>
            <th className="hidden xl:table-cell">Agendamento</th>
            <th>Oficina</th>
            <th className="hidden xl:table-cell">Aberto</th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => (
            <tr key={c.id} data-testid={`ps-case-${c.id}`} onClick={() => setOpenId(c.id)} className="cursor-pointer">
              <td>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-[2px] bg-orange-400/15 text-orange-400 flex items-center justify-center text-[9.5px] font-bold shrink-0">{initials(c.cliente)}</span>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.cliente}</p>
                    <p className="mono text-[11px] t-label">{c.id}<span className="lg:hidden"> · {c.veiculo || '—'} · VIN {c.vin || '—'}</span></p>
                  </div>
                </div>
              </td>
              <td className="hidden lg:table-cell text-foreground/75">{c.veiculo || '—'}<span className="block mono text-[10px] t-label" data-testid={`ps-row-vin-${c.id}`}>VIN {c.vin || '—'}</span></td>
              <td><div className="flex flex-col gap-1 items-start"><span className="ent-tag t-warn">{c.tipo}</span><WarrantyBadge w={c.warranty} compact /></div></td>
              <td className="hidden xl:table-cell"><span className={`ent-tag ${prioCls[c.prioridade] || ''}`}>{c.prioridade}</span></td>
              <td onClick={(e) => e.stopPropagation()}>
                <select data-testid={`ps-status-${c.id}`} value={c.status} onChange={(e) => changeStatus(c, e.target.value)} className="ent-input" style={{ width: 165 }}>
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select data-testid={`ps-work-${c.id}`} value={c.work_status || 'Não iniciado'} onChange={(e) => changeWork(c, { work_status: e.target.value })}
                  className={`ent-input ${workCls(c.work_status)}`} style={{ width: 130 }}>
                  {WORK_STATUSES.map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select data-testid={`ps-local-${c.id}`} value={c.service_location || ''} onChange={(e) => changeWork(c, { service_location: e.target.value })}
                  className="ent-input" style={{ width: 142 }}>
                  <option value="" className="bg-popover text-foreground">Informar local…</option>
                  {LOCATIONS.map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
                </select>
                {c.service_location === 'Oficina externa' && (
                  <input data-testid={`ps-shop-${c.id}`} defaultValue={c.external_shop || ''} placeholder="Nome da oficina"
                    onBlur={(e) => e.target.value !== (c.external_shop || '') && changeWork(c, { external_shop: e.target.value })}
                    className="ent-input mt-1" style={{ width: 142 }} />
                )}
              </td>
              <td className="hidden xl:table-cell">
                {c.agendado
                  ? <span className="ent-status t-ok num"><CalendarCheck className="w-3 h-3" />{formatDate(c.appointment_date)}{c.appointment_time ? ` ${c.appointment_time}` : ''}</span>
                  : <span className="t-label">—</span>}
              </td>
              <td onClick={(e) => e.stopPropagation()}><WorkshopCell c={c} /></td>
              <td className="hidden xl:table-cell text-[11px] t-label num">{c.createdBy || '—'}<br />{c.created_at ? fmtTs(c.created_at) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const GROUPS = [
    { key: 'sem', title: 'Sem agendamento', desc: 'Cliente entrou em contato — precisa de ação', bar: '#38bdf8', list: filtered.filter((c) => !c.in_workshop && !c.agendado), emptyMsg: 'Nenhum caso aguardando agendamento' },
    { key: 'agendados', title: 'Agendados', desc: 'Serviço marcado — aguardando o dia', bar: '#10b981', list: filtered.filter((c) => !c.in_workshop && c.agendado), emptyMsg: 'Nenhum serviço agendado' },
    { key: 'oficina', title: 'Na oficina', desc: 'Carro em serviço — contador rodando', bar: '#f97316', list: filtered.filter((c) => c.in_workshop), emptyMsg: 'Nenhum carro na oficina' },
  ];

  return (
    <div className="space-y-3" data-testid="post-sale-page">
      <div className="ent-ribbon" data-testid="ps-ribbon">
        <div><p className="ent-label">Casos ativos</p><p className="ent-value mt-1" data-testid="ps-stat-ativos">{ativos.length}</p></div>
        <div><p className="ent-label">Sem agendamento</p><p className={`ent-value mt-1 ${semAgenda.length ? 't-warn' : ''}`}>{semAgenda.length}</p></div>
        <div><p className="ent-label">Agendados</p><p className="ent-value mt-1">{agendados.length}</p></div>
        <div><p className="ent-label">Na oficina</p><p className={`ent-value mt-1 ${naOficina.length ? 't-warn' : ''}`}>{naOficina.length}</p></div>
        <div><p className="ent-label">Em execução</p><p className={`ent-value mt-1 ${emExecucao.length ? 't-warn' : ''}`} data-testid="ps-stat-execucao">{emExecucao.length}</p></div>
        <div><p className="ent-label">Oficina externa</p><p className="ent-value mt-1" data-testid="ps-stat-externa">{externos.length}</p></div>
        <div><p className="ent-label">Prioridade alta</p><p className={`ent-value mt-1 ${ativos.filter((c) => c.prioridade === 'Alta').length ? 't-bad' : ''}`}>{ativos.filter((c) => c.prioridade === 'Alta').length}</p></div>
        <div><p className="ent-label">Entregues</p><p className="ent-value mt-1 t-ok">{entregues.length}</p></div>
        <div><p className="ent-label">Média na oficina</p><p className="ent-value mt-1">{avgHours}{avgHours !== '—' ? 'h' : ''}</p></div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="ent-tabs">
          <button data-testid="ps-tab-ativos" onClick={() => setViewTab('ativos')} className={`ent-tab ${viewTab === 'ativos' ? 'ent-tab-active' : ''}`}><ClipboardList className="w-3.5 h-3.5" /> Ativos ({ativos.length})</button>
          <button data-testid="ps-tab-arquivados" onClick={() => setViewTab('arquivados')} className={`ent-tab ${viewTab === 'arquivados' ? 'ent-tab-active' : ''}`}><Archive className="w-3.5 h-3.5" /> Arquivados ({arquivados.length})</button>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
            <input data-testid="ps-search" className="ent-input pl-8" style={{ width: 220 }} placeholder="Cliente, veículo, tipo, ID..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="ps-opened-by" value={openedBy} onChange={(e) => setOpenedBy(e.target.value)} className="ent-input" style={{ width: 190 }}>
            <option value="">Aberto por: todos</option>
            {openers.map(([name, n]) => <option key={name} value={name} className="bg-popover text-foreground">{`${name} (${n})`}</option>)}
          </select>
          <button data-testid="ps-new-btn" onClick={() => setDOpen(true)} className="ent-btn ent-btn-primary whitespace-nowrap"><Plus className="w-3.5 h-3.5" /> Novo caso</button>
        </div>
      </div>

      {viewTab === 'ativos' ? (
        <div className="space-y-3">
          {GROUPS.map((g) => (
            <div key={g.key} className="ent-panel overflow-hidden" data-testid={`ps-group-${g.key}`}>
              <div className="ent-colhead" style={{ borderTop: `2px solid ${g.bar}` }}>
                <span className="w-[3px] h-3.5 rounded-[1px]" style={{ background: g.bar }} />
                <span className="ent-coltitle uppercase tracking-wide">{g.title}</span>
                <span className="ent-pill" data-testid={`ps-group-count-${g.key}`}>{g.list.length}</span>
                <span className="text-[11px] t-label ml-auto hidden sm:block">{g.desc}</span>
              </div>
              {g.list.length === 0 ? <p className="ent-empty">{g.emptyMsg}</p> : <CaseTable list={g.list} />}
            </div>
          ))}
        </div>
      ) : (
        <div className="ent-panel overflow-hidden">
          <div className="ent-colhead" style={{ borderTop: '2px solid #6b7280' }}>
            <Archive className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="ent-coltitle uppercase tracking-wide">Casos arquivados</span>
            <span className="ent-pill">{filtered.length}</span>
          </div>
          {filtered.length === 0 ? <p className="ent-empty">Nenhum caso arquivado</p> : <CaseTable list={filtered} />}
        </div>
      )}

      <NewCaseDialog open={dOpen} onClose={() => setDOpen(false)} onCreated={() => load()} />

      {openId && <CaseWorkspace caseId={openId} cases={cases} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
